import { useMutation, useQuery } from "@apollo/client";
import Button from "components/base/Button";
import SearchBar from "components/base/SearchBar";
import Status from "components/base/Status";
import { Alert } from "components/base/Toast";
import { useModals } from "ModalProvider";
import moment from "moment";
import { useState } from "react";
import { FaLink, FaLinkSlash } from "react-icons/fa6";
import { LiaChartLineSolid } from "react-icons/lia";
import { searchByProp } from "utils/search";
import {
  DELETE_PRICE_LIST_ITEMS,
  FETCH_ODOO_PRODUCT_AVG_SALES_PRICE,
} from "./graphql";
import LinkOdooProductView from "./LinkOdooProductView";

const PriceListDetail = ({
  priceListId,
  company,
  products,
  currency,
  editProduct,
  isInc,
}) => {
  const [searchText, setSearchText] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  const fetchOdooProductAvgSalesPrice = useQuery(
    FETCH_ODOO_PRODUCT_AVG_SALES_PRICE,
  );

  const [deleteProductSalesPrice, deleteProductSalesPriceRes] = useMutation(
    DELETE_PRICE_LIST_ITEMS,
    {
      onCompleted() {
        Alert("success", "Saved.");
        setSelectedIds([]);
      },
      onError(error) {
        Alert("error", error.message);
      },
      refetchQueries: ["FETCH_PRICE_LIST"],
      awaitRefetchQueries: true,
    },
  );
  const modal = useModals();

  const odooAvgPrices = fetchOdooProductAvgSalesPrice.data
    ? JSON.parse(
        fetchOdooProductAvgSalesPrice.data
          .odooProductAvgSalesPriceForLast365Days,
      )
    : null;

  const allProducts = products.map((product) => {
    let odooAvgPrice = 0.0;
    if (odooAvgPrices) {
      const found = odooAvgPrices.find((x) => x.product_id === product.odooId);
      if (found) odooAvgPrice = found.avg_price;
    }
    return { ...product, odooAvgPrice };
  });

  const productsToShow = allProducts.filter((i) =>
    searchByProp(i, ["name", "number"], searchText),
  );

  function editHandler(product) {
    editProduct({
      product,
      price: product.price.price,
      discountPercentage: product.price.discountPercentage,
      useFixedMargin: product.price.useFixedMargin,
      fixedMargin: product.price.fixedMargin,
      state: product.price.state,
    });
  }

  function tryDelete() {
    if (
      window.confirm(
        `Are you sure to remove ${selectedIds.length} product${
          selectedIds.length > 1 ? "s" : ""
        } from this price list?` +
          "\nThis will not delete the products, just remove them from this price list.",
        "\nYou can add them back later.",
      )
    ) {
      deleteProductSalesPrice({
        variables: { priceListId, productIds: selectedIds },
      });
    }
  }

  function chooseProductToLink(product) {
    modal.present({
      title: "Choose INC Odoo Product to Link",
      subtitle: `[${product.number}] ${product.name}`,
      children: (
        <LinkOdooProductView
          instance="INC"
          product={product}
          hide={modal.hide}
        />
      ),
    });
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 flex flex-col overflow-auto card p-0 whitespace-nowrap">
        <div className="p-4">
          <SearchBar
            className="bg-gray-100 dark:bg-black dark:bg-opacity-30"
            onChange={setSearchText}
            placeholder="Search by name, item number."
          />
        </div>

        <div className="flex-1 overflow-auto">
          <table>
            <thead>
              <tr className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-800">
                <th className="py-4 px-4 w-1/2">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={
                        selectedIds.length === productsToShow.length &&
                        productsToShow.length > 0
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedIds(productsToShow.map((i) => i.id));
                        } else {
                          setSelectedIds([]);
                        }
                      }}
                    />
                    {selectedIds.length > 0 ? (
                      <div className="flex items-center space-x-3">
                        <div>{selectedIds.length} products selected</div>
                        <Button
                          danger
                          onClick={tryDelete}
                          loading={deleteProductSalesPriceRes.loading}
                        >
                          Remove
                        </Button>
                        <Button onClick={() => setSelectedIds([])}>
                          Clear
                        </Button>
                      </div>
                    ) : (
                      <div>Product ({products.length})</div>
                    )}
                  </div>
                </th>
                <th className="text-right">Cost (Default qty, {currency})</th>
                <th className="text-right">Price ({currency})</th>
                <th className="text-right">Discount</th>
                {odooAvgPrices ? (
                  <th className="text-right text-purple-600">Odoo Avg Price</th>
                ) : null}
                <th className="text-right px-4">Margin</th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4">
                    <label>No products in this price list.</label>
                  </td>
                </tr>
              ) : (
                productsToShow.map((product) => (
                  <tr
                    key={product.id}
                    className={`border-y border-gray-100 dark:border-gray-700
                      ${isInc && !product.odooIdInc ? "bg-red-50 dark:bg-red-900" : ""}
                      ${
                        product.price.state === "PENDING"
                          ? "bg-orange-50 dark:bg-orange-900"
                          : ""
                      }
                      `}
                  >
                    <td className="px-4">
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(product.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedIds([...selectedIds, product.id]);
                            } else {
                              setSelectedIds(
                                selectedIds.filter((id) => id !== product.id),
                              );
                            }
                          }}
                        />

                        <div className="flex justify-between items-center">
                          <div className="flex space-x-3 items-center">
                            <div>
                              <div className="flex items-center space-x-2">
                                <Button
                                  edit
                                  onClick={() => editHandler(product)}
                                  disabled={!editProduct}
                                >
                                  {product.number}
                                  {product.version
                                    ? ` | v${product.version}`
                                    : ""}
                                </Button>

                                {isInc && (
                                  <Button
                                    odoo
                                    danger={!product.odooIdInc}
                                    onClick={() => chooseProductToLink(product)}
                                  >
                                    {product.odooIdInc ? (
                                      <FaLink />
                                    ) : (
                                      <FaLinkSlash />
                                    )}
                                  </Button>
                                )}

                                {product.images.length > 0 && (
                                  <div className="flex space-x-2 items-center">
                                    {product.images.map((i, index) => (
                                      <img
                                        key={index}
                                        src={i.url}
                                        alt=""
                                        className="h-4 bg-gray-200 rounded"
                                      />
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div className="opacity-70 text-xs whitespace-normal">
                                {product.company !== "UNSET"
                                  ? `(${product.company})`
                                  : null}
                                {product.name} ({product.countryCodeOfOrigin})
                              </div>

                              {product.company !== "UNSET" &&
                                product.company !== company && (
                                  <div className="font-semibold text-red-600">
                                    ⚠️ This product belongs to {product.company}
                                    , not {company}. Please remove it from this
                                    price list.
                                  </div>
                                )}

                              {isInc && !product.odooIdInc && (
                                <div className="font-semibold text-red-600">
                                  Not onnected to INC Odoo.
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="text-right">
                      {product.productCost ? (
                        <div className="flex space-x-2 items-center justify-end">
                          <div className="opacity-70 text-xs">
                            {product.productCost.date
                              ? moment(product.productCost.date).format(
                                  "YYYY-MM-DD",
                                )
                              : null}
                          </div>
                          <div className="w-12">
                            {product.productCost.cost?.toFixed(3)}
                          </div>
                        </div>
                      ) : (
                        "-"
                      )}
                    </td>

                    <td className="text-right">
                      <div className="flex space-x-2 items-center justify-end">
                        <div className="opacity-70 text-xs">
                          {moment(product.price.updatedAt).format("YYYY-MM-DD")}
                        </div>

                        {product.price.useFixedMargin ? (
                          <LiaChartLineSolid className="text-teal-600" />
                        ) : null}

                        <div className="flex items-center justify-end">
                          <div className="flex space-x-1">
                            {product.price.state === "PENDING" && (
                              <Status
                                inline
                                status={product.price.state.toUpperCase()}
                              />
                            )}
                            <div>{product.price.finalPrice.toFixed(2)}</div>
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="text-right">
                      {product.price.discountPercentage}%
                    </td>

                    {odooAvgPrices ? (
                      <td className="text-right">
                        {product.odooAvgPrice
                          ? product.odooAvgPrice.toFixed(2)
                          : "0.00"}
                      </td>
                    ) : null}
                    <td className="text-right px-4">
                      {product.margin?.toFixed(2)}%
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 opacity-70 mx-1">
        <div className="flex items-center space-x-1">
          <span>
            <LiaChartLineSolid className="text-teal-600" size={16} />
          </span>
          <span>indicates the price is calculated by fixed margin.</span>
        </div>
        <div>
          You can import a excel file to batch update prices. You must export
          the current price list first to get the correct format. Then use the
          column "N" for new prices, and "O" for new discount percentage.
        </div>
        {odooAvgPrices ? (
          <div>
            Odoo Avg Price is the average price from odoo invoice for last 365
            days.
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default PriceListDetail;
